import { useState } from 'react';
import { cn } from '@/lib/cn';
import { formatPaise, timeAgo } from '@/lib/format';
import { Button } from '@/components/ui/button';
import { Dialog } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { apiErrorMessage } from '@shared/api/client';
import type { PayoutStatus, Uuid } from '@shared/types/api';
import {
  useAdminPayouts,
  useCancelPayout,
  useCreatePayout,
  useHoldPayout,
  useMarkPayoutPaid,
} from '@/hooks/use-admin';
import { useSectionPermission } from '@/hooks/use-admin-permissions';

const TABS: { value: PayoutStatus | 'ALL'; label: string }[] = [
  { value: 'ALL', label: 'All' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'ON_HOLD', label: 'On hold' },
  { value: 'PAID', label: 'Paid' },
  { value: 'CANCELLED', label: 'Cancelled' },
];

const TONE: Record<PayoutStatus, string> = {
  PENDING: 'bg-amber-50 text-amber-800',
  ON_HOLD: 'bg-orange-50 text-orange-800',
  PAID: 'bg-brand-50 text-brand-800',
  CANCELLED: 'bg-red-50 text-red-700',
};

type OpenPanel =
  | { kind: 'create' }
  | { kind: 'pay'; id: Uuid }
  | { kind: 'hold'; id: Uuid }
  | { kind: 'cancel'; id: Uuid }
  | null;

export function AdminPayoutsPage() {
  const [tab, setTab] = useState<PayoutStatus | 'ALL'>('PENDING');
  const [panel, setPanel] = useState<OpenPanel>(null);
  const { canCreate, canEdit, canDelete, isSuperAdmin } = useSectionPermission('PAYOUTS');
  const hasAnyAction = canEdit || canDelete || isSuperAdmin;

  const { data, isLoading } = useAdminPayouts({
    status: tab === 'ALL' ? undefined : tab,
    pageSize: 50,
  });

  const items = data?.items ?? [];

  return (
    <div className="w-full px-4 py-6 md:px-6 md:py-8">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
          <h1 className="text-xl font-semibold text-gray-900">Payouts</h1>
          <p className="text-sm text-gray-600">
            Generate payouts for completed transactions; mark paid after NEFT/UPI settlement.
          </p>
        </div>
        {(canCreate || isSuperAdmin) && (
          <Button onClick={() => setPanel({ kind: 'create' })}>New payout</Button>
        )}
      </div>

      <div className="mt-5 flex items-center gap-1.5 overflow-x-auto no-scrollbar scroll-smooth py-1 -mx-4 px-4 sm:mx-0 sm:px-0 sm:flex-wrap border-b border-gray-200 pb-2">
        {TABS.map((t) => (
          <button
            key={t.value}
            onClick={() => setTab(t.value)}
            className={cn(
              'shrink-0 rounded-full px-3 py-1 text-xs font-medium transition-colors whitespace-nowrap',
              tab === t.value
                ? 'bg-brand-700 text-white shadow-xs'
                : 'bg-white text-gray-600 hover:bg-gray-50 border border-gray-200/60 sm:border-0',
            )}
          >
            {t.label}
          </button>
        ))}
      </div>

      <section className="mt-4 overflow-hidden rounded-lg border border-gray-200 bg-white shadow-xs">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[750px] text-sm text-left">
            <thead className="bg-gray-50 text-xs uppercase tracking-wide text-gray-500 border-b border-gray-200">
              <tr>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Tx</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Associate</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Gross</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">TDS</th>
                <th className="px-4 py-3 text-right font-medium whitespace-nowrap">Net</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Status</th>
                <th className="px-4 py-3 font-medium whitespace-nowrap">Created</th>
                {hasAnyAction ? <th className="px-4 py-3 text-right font-medium pr-6 whitespace-nowrap">Actions</th> : null}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {isLoading ? (
                <tr><td colSpan={hasAnyAction ? 8 : 7} className="p-6 text-center text-gray-500">Loading…</td></tr>
              ) : items.length === 0 ? (
                <tr><td colSpan={hasAnyAction ? 8 : 7} className="p-6 text-center text-gray-500">No payouts.</td></tr>
              ) : items.map((p) => (
                <tr key={p.id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 font-mono text-xs whitespace-nowrap font-medium text-gray-900">{p.transactionId.slice(0, 8)}…</td>
                  <td className="px-4 py-3 font-mono text-xs text-gray-600 whitespace-nowrap">{p.associateUserId.slice(0, 8)}…</td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">{formatPaise(p.grossAmountPaise)}</td>
                  <td className="px-4 py-3 text-right text-gray-600 whitespace-nowrap">
                    {formatPaise(p.tdsAmountPaise)} <span className="text-[10px] text-gray-400">({p.tdsPercentage}%)</span>
                  </td>
                  <td className="px-4 py-3 text-right font-semibold whitespace-nowrap">{formatPaise(p.netAmountPaise)}</td>
                  <td className="px-4 py-3 whitespace-nowrap">
                    <span className={cn('inline-flex rounded-full px-2 py-0.5 text-[10px] font-medium uppercase', TONE[p.status])}>
                      {p.status.replace(/_/g, ' ')}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-xs text-gray-500 whitespace-nowrap">{timeAgo(p.createdAt)}</td>
                  {hasAnyAction ? (
                    <td className="px-4 py-3 text-right whitespace-nowrap pr-6">
                      <div className="flex flex-wrap justify-end gap-2 text-xs">
                        {p.status === 'PENDING' ? (
                          <>
                            {(canEdit || isSuperAdmin) && (
                              <button className="text-brand-700 hover:underline" onClick={() => setPanel({ kind: 'pay', id: p.id })}>Mark paid</button>
                            )}
                            {(canEdit || isSuperAdmin) && (
                              <button className="text-gray-700 hover:underline" onClick={() => setPanel({ kind: 'hold', id: p.id })}>Hold</button>
                            )}
                            {(canDelete || isSuperAdmin) && (
                              <button className="text-red-600 hover:underline" onClick={() => setPanel({ kind: 'cancel', id: p.id })}>Cancel</button>
                            )}
                          </>
                        ) : p.status === 'ON_HOLD' ? (
                          <>
                            {(canEdit || isSuperAdmin) && (
                              <button className="text-brand-700 hover:underline" onClick={() => setPanel({ kind: 'pay', id: p.id })}>Mark paid</button>
                            )}
                            {(canDelete || isSuperAdmin) && (
                              <button className="text-red-600 hover:underline" onClick={() => setPanel({ kind: 'cancel', id: p.id })}>Cancel</button>
                            )}
                          </>
                        ) : null}
                      </div>
                    </td>
                  ) : null}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {panel?.kind === 'create' ? (
        <CreatePayoutDialog onClose={() => setPanel(null)} />
      ) : null}
      {panel?.kind === 'pay' ? (
        <MarkPaidDialog id={panel.id} onClose={() => setPanel(null)} />
      ) : null}
      {panel?.kind === 'hold' ? (
        <NotesDialog title="Hold payout" kind="hold" id={panel.id} onClose={() => setPanel(null)} />
      ) : null}
      {panel?.kind === 'cancel' ? (
        <NotesDialog title="Cancel payout" kind="cancel" id={panel.id} onClose={() => setPanel(null)} />
      ) : null}
    </div>
  );
}

function CreatePayoutDialog({ onClose }: { onClose: () => void }) {
  const [txId, setTxId] = useState('');
  const create = useCreatePayout();
  return (
    <Dialog
      open
      onClose={onClose}
      title="New payout"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => create.mutate(txId, { onSuccess: onClose })}
            disabled={!/^[0-9a-f-]{36}$/.test(txId) || create.isPending}
          >
            {create.isPending ? 'Creating…' : 'Create'}
          </Button>
        </>
      }
    >
      <p className="text-sm text-gray-700">
        Transaction must be FINAL_DOC_READY or later with a PAID invoice.
      </p>
      <Input className="mt-3" value={txId} onChange={(e) => setTxId(e.target.value)} placeholder="Transaction UUID" />
      {create.isError ? <p className="mt-2 text-sm text-red-600">{apiErrorMessage(create.error)}</p> : null}
    </Dialog>
  );
}

function MarkPaidDialog({ id, onClose }: { id: Uuid; onClose: () => void }) {
  const [ref, setRef] = useState('');
  const [notes, setNotes] = useState('');
  const mark = useMarkPayoutPaid();
  return (
    <Dialog
      open
      onClose={onClose}
      title="Mark payout paid"
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() =>
              mark.mutate(
                { id, transactionReference: ref.trim(), notes: notes.trim() || undefined },
                { onSuccess: onClose },
              )
            }
            disabled={ref.trim().length < 3 || mark.isPending}
          >
            {mark.isPending ? 'Saving…' : 'Confirm'}
          </Button>
        </>
      }
    >
      <div className="space-y-3 text-sm">
        <Labelled label="Bank / UPI reference">
          <Input value={ref} onChange={(e) => setRef(e.target.value)} placeholder="UTR1234567890" />
        </Labelled>
        <Labelled label="Notes (optional)">
          <Input value={notes} onChange={(e) => setNotes(e.target.value)} />
        </Labelled>
        {mark.isError ? <p className="text-sm text-red-600">{apiErrorMessage(mark.error)}</p> : null}
      </div>
    </Dialog>
  );
}

function NotesDialog({
  title,
  kind,
  id,
  onClose,
}: {
  title: string;
  kind: 'hold' | 'cancel';
  id: Uuid;
  onClose: () => void;
}) {
  const [notes, setNotes] = useState('');
  const hold = useHoldPayout();
  const cancel = useCancelPayout();
  const m = kind === 'hold' ? hold : cancel;
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      footer={
        <>
          <Button variant="ghost" onClick={onClose}>Cancel</Button>
          <Button
            onClick={() => {
              if (notes.trim().length < 3) return;
              const payload = { id, notes: notes.trim() };
              (kind === 'hold' ? hold : cancel).mutate(payload, { onSuccess: onClose });
            }}
            disabled={notes.trim().length < 3 || m.isPending}
          >
            {m.isPending ? 'Working…' : 'Confirm'}
          </Button>
        </>
      }
    >
      <Input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Notes (min 3 chars)" />
      {m.isError ? <p className="mt-2 text-sm text-red-600">{apiErrorMessage(m.error)}</p> : null}
    </Dialog>
  );
}

function Labelled({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <div className="mb-1 text-xs font-medium text-gray-700">{label}</div>
      {children}
    </label>
  );
}
